<template>
	<div>
		<!-- Display debugging info in development mode -->
		<div v-if="false" style="font-size: 10px; color: #999;">
			ID: {{ playerId }}, Type: {{ itemType }}, Fav: {{ isFavorite ? 'Yes' : 'No' }}
		</div>
		
		<button 
			class="favorite-button" 
			:class="{ 
				'favorite': isFavorite,
				'at-limit': !isFavorite && isAtPlayersLimit && itemType === 'player'
			}" 
			@click="toggleFavorite"
			:data-player-id="playerId"
			:ref="!isFavorite && isAtPlayersLimit && itemType === 'player' ? 'tooltipTrigger' : null"
		>
			<span class="star-icon">
				<span class="vh">
					{{ isFavorite ? removeFromFavoritesText : addToFavoritesText }}
				</span>
				<span v-if="isFavorite" v-html="starFull"></span>
				<span v-else v-html="starEmpty"></span>
			</span>
		</button>
	</div>
</template>

<script>
import { db, auth } from '../firebase';
import { doc, getDoc, updateDoc, setDoc, arrayUnion, arrayRemove, serverTimestamp, onSnapshot } from 'firebase/firestore';
import favoritesManager from '../firebase/favoritesManager';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light.css';

export default {
	name: 'FavoriteButton',
	props: {
		playerId: {
			type: [String, Number],
			required: true
		},
		userId: {
			type: String,
			default: ''
		},
		initialIsFavorite: {
			type: Boolean,
			default: false
		},
		addToFavoritesText: {
			type: String,
			default: 'Add to favorites'
		},
		removeFromFavoritesText: {
			type: String,
			default: 'Remove from favorites'
		},
		itemType: {
			type: String,
			default: 'player' // Can be 'player' or 'activity'
		}
	},
	data() {
		// console.log('FavoriteButton created with itemType:', this.itemType, 'and playerID:', this.playerId);
		
		// Normalize the player ID - prioritize numeric IDs for activities
		let normalizedId = String(this.playerId);
		if (this.itemType === 'activity' && this.playerId) {
			// For activities, always use numeric IDs if possible
			if (!isNaN(parseInt(this.playerId, 10))) {
				normalizedId = String(parseInt(this.playerId, 10));
				// console.log('Using numeric activity ID:', normalizedId);
			} else if (typeof this.playerId === 'string' && this.playerId.startsWith('activity-')) {
				// Extract numeric ID from activity-{id} format if possible
				const match = this.playerId.match(/activity-(\d+)/);
				if (match && match[1]) {
					normalizedId = match[1];
					// console.log('Extracted numeric ID from activity string:', normalizedId);
				} else {
					// console.log('Using string activity ID (no numeric ID found):', normalizedId);
				}
			}
		}
		
		return {
			isFavorite: this.initialIsFavorite,
			isProcessing: false,
			isAtPlayersLimit: false,
			currentFavoritePlayers: [], // Track current favorite players for limit checking
			playerIdString: normalizedId,
			userIdString: String(this.userId),
			starFull: window.ico_star_full,
			starEmpty: window.ico_star_empty,
			unsubscribe: null,
			favoritesManagerListener: null,
			tooltipInstance: null
		};
	},
	computed: {
		// Determine the field name based on the item type
		fieldName() {
			return this.itemType === 'activity' ? 'favoriteActivities' : 'favoritePlayers';
		},
		// Determine the timestamp field name based on the item type
		timestampFieldName() {
			return this.itemType === 'activity' ? 'favoriteActivitiesLastUpdate' : 'favoritePlayersLastUpdate';
		}
	},
	mounted() {
		this.setupAuthListener();
		this.setupFavoritesManagerListener();
	},
	beforeDestroy() {
		if (this.unsubscribe) {
			this.unsubscribe();
		}
		if (this.favoritesManagerListener) {
			favoritesManager.removeEventListener(this.favoritesManagerListener);
		}
		this.destroyTooltip();
	},
	updated() {
		this.$nextTick(() => {
			this.updateTooltip();
		});
	},
	methods: {
		// Helper to ensure IDs are always strings
		ensureString(value) {
			return value !== null && value !== undefined ? String(value) : '';
		},
		
		setupAuthListener() {
			// Listen for global auth state change event
			document.addEventListener('cincyAuthStateChanged', (event) => {
				if (event.detail.isAuthenticated) {
					this.userIdString = String(event.detail.user.uid);
					this.setupRealTimeListener();
				} else {
					// Clear user ID and remove any real-time listeners
					this.userIdString = '';
					if (this.unsubscribe) {
						this.unsubscribe();
						this.unsubscribe = null;
					}
					
					// Reset favorite status to initial value if no user is logged in
					this.isFavorite = this.initialIsFavorite;
				}
			});
		
			// Initial check from the global auth state (in case event was already fired)
			if (window.cincyAuthState && window.cincyAuthState.initialized) {
				if (window.cincyAuthState.isAuthenticated) {
					this.userIdString = String(window.cincyAuthState.user.uid);
					this.setupRealTimeListener();
				} else {
					this.userIdString = '';
					this.isFavorite = this.initialIsFavorite;
				}
			}
		},
		
		setupFavoritesManagerListener() {
			// Listen for favorites manager events
			this.favoritesManagerListener = (event, data) => {
				if (event === 'processing-state-changed') {
					const normalizedId = favoritesManager.normalizeId(this.playerIdString, this.itemType);
					if (data.type === this.itemType && String(data.id) === String(normalizedId)) {
						this.isProcessing = data.isProcessing;
					}
				} else if (event === 'players-limit-changed' && this.itemType === 'player') {
					// Update limit status when it changes
					this.isAtPlayersLimit = data.isAtLimit;
				}
			};
			
			favoritesManager.addEventListener(this.favoritesManagerListener);
		},
		
		setupRealTimeListener() {
			if (!this.userIdString) return;
			
			try {
				const userDocRef = doc(db, 'userData', this.userIdString);
				
				// First, check if we need to migrate string-based activity IDs to numeric ones
				if (this.itemType === 'activity') {
					this.migrateStringActivityIds(userDocRef);
				}
				
				// Set up real-time listener
				this.unsubscribe = onSnapshot(userDocRef, (docSnap) => {
					if (docSnap.exists()) {
						const userData = docSnap.data();
						const favorites = userData[this.fieldName] || [];
						
						// Update current favorite players for limit checking
						if (this.itemType === 'player') {
							this.currentFavoritePlayers = userData.favoritePlayers || [];
							// Check if at players limit
							this.isAtPlayersLimit = favoritesManager.isAtPlayersLimit(this.currentFavoritePlayers);
						}
						
						// Check for favorite status with proper type handling
						let firebaseFavoriteState = false;
						
						if (this.itemType === 'activity') {
							// For activities, check both numeric and string forms
							const numericId = parseInt(this.playerIdString, 10);
							if (!isNaN(numericId)) {
								// If we have a numeric ID, check for both number and string versions
								firebaseFavoriteState = favorites.some(id => 
									id === numericId || // Exact numeric match
									String(id) === String(numericId) || // String comparison of numeric IDs
									String(id) === this.playerIdString // Full string match
								);
							} else {
								// For pure string IDs, just do string comparison
								firebaseFavoriteState = favorites.some(id => String(id) === this.playerIdString);
							}
						} else {
							// For players, always use string comparison
							firebaseFavoriteState = favorites.some(id => String(id) === this.playerIdString);
						}
						
						// Get the local state from favorites manager (includes pending changes)
						const localState = favoritesManager.getLocalFavoriteState(
							this.playerIdString, 
							this.itemType, 
							firebaseFavoriteState
						);
						
						// Update favorite status with local state
						this.isFavorite = localState;
					} else {
						// No document exists, reset states
						if (this.itemType === 'player') {
							this.currentFavoritePlayers = [];
							this.isAtPlayersLimit = false;
						}
					}
				}, (error) => {
					console.error('Error in real-time listener:', error);
				});
			} catch (error) {
				console.error('Error setting up real-time listener:', error);
			}
		},
		
		// Migration function to convert string-based activity IDs to numeric IDs
		async migrateStringActivityIds(userDocRef) {
			try {
				const docSnap = await getDoc(userDocRef);

				if (!docSnap.exists()) return;
				
				const userData = docSnap.data();
				const favoriteActivities = userData.favoriteActivities || [];
				
				// Check if we have any string-based activity IDs that start with "activity-"
				const stringBasedIds = favoriteActivities.filter(id => 
					typeof id === 'string' && id.startsWith('activity-')
				);
				
				if (stringBasedIds.length === 0) return; // No migration needed
				
				// console.log('Found string-based activity IDs that need migration:', stringBasedIds);
				
				// Map known string IDs to their numeric equivalents
				const idMap = {
					'activity-body-alive-yoga-class': 5010, // Adding specific mapping for this activity
					'activity-comet-bluegrass-allstars': 5014,
					'activity-dj-abiyah': 5019
				};
				
				// Get the current numeric IDs
				const numericIds = favoriteActivities.filter(id => 
					!isNaN(parseInt(id, 10)) && !String(id).startsWith('activity-')
				);
				
				// Convert known string IDs to numeric, keep unknown ones as strings
				const convertedIds = [];
				stringBasedIds.forEach(id => {
					if (idMap[id]) {
						// Add the mapped numeric ID if not already in the list
						if (!numericIds.includes(idMap[id]) && !numericIds.includes(String(idMap[id]))) {
							convertedIds.push(idMap[id]);
						}
					} else {
						// Keep any unmapped string IDs
						convertedIds.push(id);
					}
				});
				
				// Combine all IDs
				const updatedFavorites = [...numericIds, ...convertedIds];
				
				// Log the conversion
				// console.log('Converting activities:', {
				// 	original: favoriteActivities,
				// 	stringBased: stringBasedIds,
				// 	numericIds: numericIds,
				// 	converted: convertedIds,
				// 	updated: updatedFavorites
				// });
				
				// Update the document with the combined set
				await updateDoc(userDocRef, {
					favoriteActivities: updatedFavorites,
					favoriteActivitiesLastUpdate: serverTimestamp()
				});
				
				// console.log('Migration completed. Updated favorites:', updatedFavorites);
			} catch (error) {
				// console.error('Error during migration:', error);
			}
		},
		
		async toggleFavorite() {
			// Check global auth state first
			// console.log('window.cincyAuthState', window.cincyAuthState);
			if (!window.cincyAuthState || !window.cincyAuthState.isAuthenticated) {
				const loginPath = window.login_link;
				window.location.href = loginPath;
				return;
			}
			
			// For players, check limit before adding
			if (!this.isFavorite && this.itemType === 'player' && this.isAtPlayersLimit) {
				// Emit limit exceeded event for UI feedback
				this.$emit('players-limit-exceeded', {
					limit: favoritesManager.getMaxPlayersLimit(),
					currentCount: favoritesManager.getLocalPlayersCount(this.currentFavoritePlayers)
				});
				return; // Don't proceed with toggle
			}
			
			// Allow toggling even during processing for immediate undo capability
			
			// console.log('=== FAVORITE TOGGLE (BATCHED) ===');
			// console.log('Item type:', this.itemType);
			// console.log('Player ID:', this.playerIdString);
			// console.log('Is currently favorite:', this.isFavorite);
			// console.log('User ID:', this.userIdString);
			
			try {
				// Emit event before removing (for UI feedback)
				if (this.isFavorite) {
					this.$emit('before-remove', { id: this.playerIdString, type: this.itemType });
				}
				
				// Use favorites manager for batched updates
				let success;
				if (this.isFavorite) {
					// Remove from favorites (pass current favorites for limit checking)
					const currentFavorites = this.itemType === 'player' ? this.currentFavoritePlayers : [];
					success = favoritesManager.removeFromFavorites(
						this.userIdString, 
						this.playerIdString, 
						this.itemType,
						currentFavorites
					);
				} else {
					// Add to favorites (pass current favorites for limit checking)
					const currentFavorites = this.itemType === 'player' ? this.currentFavoritePlayers : [];
					success = favoritesManager.addToFavorites(
						this.userIdString, 
						this.playerIdString, 
						this.itemType,
						currentFavorites
					);
				}
				
				if (success) {
					// Toggle local state for immediate feedback
					this.isFavorite = !this.isFavorite;
					
					// Update limit status for players
					if (this.itemType === 'player') {
						this.isAtPlayersLimit = favoritesManager.isAtPlayersLimit(this.currentFavoritePlayers);
					}
					
					// Emit events based on the new favorite state
					if (this.isFavorite) {
						this.$emit('added-to-favorites', { id: this.playerIdString, type: this.itemType });
					} else {
						this.$emit('removed-from-favorites', { id: this.playerIdString, type: this.itemType });
					}
				}
				
			} catch (error) {
				// console.error('Error updating favorite status:', error);
				this.$emit('favorite-error', error);
			}
		},
		
		// Tooltip management methods
		initTooltip() {
			if (this.$refs.tooltipTrigger && !this.isFavorite && this.isAtPlayersLimit && this.itemType === 'player') {
				// Destroy any existing tooltip first
				this.destroyTooltip();
				
				// Create tooltip with responsive positioning
				this.tooltipInstance = tippy(this.$refs.tooltipTrigger, {
					content: 'Maximum favorite players limit reached',
					theme: 'court-tooltip',
					placement: 'top',
					maxWidth: 300,
					arrow: true,
					interactive: false,
					appendTo: document.body,
					popperOptions: {
						modifiers: [
							{
								name: 'preventOverflow',
								options: {
									boundary: document.body,
								}
							}
						]
					}
				});
			}
		},
		
		destroyTooltip() {
			if (this.tooltipInstance) {
				this.tooltipInstance.destroy();
				this.tooltipInstance = null;
			}
			
			// Extra cleanup - check if tippy instance exists on the element itself
			if (this.$refs.tooltipTrigger && this.$refs.tooltipTrigger._tippy) {
				this.$refs.tooltipTrigger._tippy.destroy();
			}
		},
		
		updateTooltip() {
			// Check if we need to show/hide tooltip based on current state
			const shouldShowTooltip = !this.isFavorite && this.isAtPlayersLimit && this.itemType === 'player';
			
			if (shouldShowTooltip && !this.tooltipInstance) {
				this.initTooltip();
			} else if (!shouldShowTooltip && this.tooltipInstance) {
				this.destroyTooltip();
			}
		}
	}
};
</script>

<style scoped>
.favorite-button.processing {
	/* Show visual feedback but keep button clickable */
	opacity: 0.8;
}

.favorite-button.processing .star-icon {
	animation: pulse 1.5s ease-in-out infinite;
}

.favorite-button.at-limit {
	/* Style for when player favorites limit is reached */
	opacity: 0.5;
	cursor: not-allowed;
}

@keyframes pulse {
	0% {
		opacity: 1;
	}
	50% {
		opacity: 0.5;
	}
	100% {
		opacity: 1;
	}
}
</style>